import {useProposition} from '@myparcel-frontend/proposition';
import {storeToRefs} from 'pinia';
import useAccount from '@/composables/useAccount/useAccount';
import useAclRoles from '@/composables/useAclRoles/useAclRoles';
import useShop from '@/composables/useShop/useShop';
import useSubscription from '@/composables/useSubscription';
import {useEnvironmentStore} from '@/env/useEnvironmentStore';
import {useConfigStore} from '@/store/config';
import {convertTierIdToName} from '@/types/subscriptions/tiers';
import logToMonitor from '@/utils/logging';

interface UseAppcues {
  updateUserIdentity: (locale: string) => Promise<Record<string, unknown> | false>;
  initAppcues: (locale: string) => Promise<boolean>;
  isInitialized: () => boolean;
  trackAppcuesPage: () => void;
}

export default function useAppcues(): UseAppcues {
  const appCuesScriptId = 'appcues-script-loader';
  const {proposition} = useProposition();
  const configStore = useConfigStore();
  const {setAppcuesIsReady} = configStore;
  const {appcuesIsReady} = storeToRefs(configStore);
  const {account, currentUser} = useAccount();
  const {currentShop} = useShop();
  const {getConvertedCustomerRoles} = useAclRoles();
  const {environment} = useEnvironmentStore();

  const isInitialized = (): boolean => {
    return appcuesIsReady.value;
  };

  const createScript = (): HTMLScriptElement => {
    const script = document.createElement('script');
    script.src = `//fast.appcues.com/${proposition.value.tools.appcues.id}.js`;
    script.id = appCuesScriptId;

    return script;
  };

  const elementExists = (): boolean => {
    return !!window.Appcues && !!Object.keys(window.Appcues).length;
  };

  const getSubscriptionsValues = () => {
    const {activeSubscriptions, trialActiveSubscriptions, endingSoonSubscriptions} = useSubscription();
    return [...activeSubscriptions.value, ...trialActiveSubscriptions.value, ...endingSoonSubscriptions.value].reduce(
      (accumulator, {type, tier}) => {
        const tierName = convertTierIdToName(tier);
        accumulator[`has_${type}_${tierName}_subscription`] = true;
        return accumulator;
      },
      {
        has_my_contracts_light_subscription: false,
        has_my_contracts_start_subscription: false,
        has_my_contracts_plus_subscription: false,
        has_my_contracts_premium_subscription: false,
        has_my_contracts_max_subscription: false,
        has_my_analytics_start_subscription: false,
        has_my_analytics_plus_subscription: false,
        has_my_analytics_premium_subscription: false,
        has_my_orders_start_subscription: false,
        has_bundle_start_subscription: false,
        has_bundle_plus_subscription: false,
        has_bundle_premium_subscription: false,
        has_bundle_max_subscription: false,
      },
    );
  };

  const updateUserIdentity = async (locale: string): Promise<Record<string, unknown> | false> => {
    if (!proposition.value.tools.appcues.enabled || !proposition.value.tools.appcues.id || !elementExists()) {
      return false;
    }

    const sendsTrackAndTrace = !!currentShop.value?.tracktrace?.send_track_trace_emails;
    const usesPortal = !!currentShop.value?.branding?.use_consumer_portal;
    const usesTrackAndTracePage = !!currentShop.value?.branding?.enable_track_trace;
    const usesTheReturnButton = !!currentShop.value?.branding?.allow_creating_related_returns;
    const usesTheReturnPage = !!currentShop.value?.branding?.enable_returns;

    const convertedRoles = await getConvertedCustomerRoles();

    await window.Appcues.identify(currentUser.value.id, {
      ...convertedRoles,
      ...getSubscriptionsValues(),
      sends_track_and_trace: sendsTrackAndTrace,
      uses_portal: usesPortal,
      uses_track_and_trace_page: usesTrackAndTracePage,
      uses_the_return_button: usesTheReturnButton,
      uses_the_return_page: usesTheReturnPage,
      user_id: currentUser.value.id,
      has_terms_agreed: account.value.terms_agreed,
      account_created_date: account.value.created,
      user_created_date: currentUser.value.created,
      first_name: account.value.first_name,
      last_name: account.value.last_name,
      account_id: account.value.id,
      shop_id: currentShop.value.id,
      language: locale.toUpperCase(),
    });

    const user = await window.Appcues.user();

    return user;
  };

  const initAppcues = (locale: string): Promise<boolean> => {
    if (environment.devMode) {
      console.warn('Appcues was not initialized because of devMode');
      return new Promise((resolve) => resolve(false));
    }

    if (!proposition.value.tools.appcues.enabled) {
      console.warn('Appcues was not initialized because it is disabled');
      return new Promise((resolve) => resolve(false));
    }

    if (!proposition.value.tools.appcues.id) {
      throw new Error('"appCuesAccountId" is not defined');
    }

    /* istanbul ignore next */
    return new Promise((resolve) => {
      if (elementExists()) {
        void updateUserIdentity(locale).then(() => {
          setAppcuesIsReady();
          resolve(true);
        });
        return;
      }

      const script = createScript();
      document.body.appendChild(script);

      /**
       * This AppCues event listener will run async, which is fine.
       * We don't want the ui to hold loading for these statistics to be ready.
       */
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      script.addEventListener('load', async () => {
        await updateUserIdentity(locale);

        setAppcuesIsReady();

        resolve(true);
      });

      script.addEventListener('error', (event) => {
        /**
         * This error will not be caught by the catcher below, due to the async nature.
         * We do not want AppCues to delay the rest of the page loading.
         */
        logToMonitor(Error(`Could not load AppCues dynamic script: ${event}`), {
          _myparcel_extra_info: 'Loading Appcues dynamic script failed in useAppcues composable',
        });
        resolve(false);
      });
    });
  };

  const trackAppcuesPage = (): void => {
    if (isInitialized()) {
      window.Appcues?.page?.();
    }
  };

  return {
    updateUserIdentity,
    initAppcues,
    isInitialized,
    trackAppcuesPage,
  };
}
